// The cinematic frame around a replay (letterbox, vignette, film grain), its title and end cards, and the welcome
// card that offers the best-known replays on a first visit.

import { useEffect, useMemo, useState } from "react";
import { audio } from "../fx/audio";
import { motionFx, useFx } from "../fx/state";
import { durationLabel, yearsLabel } from "../lib/time";
import { fmtInt } from "../lib/format";
import { startReplay, restartReplay } from "../lib/replayEngine";
import { useApp } from "../lib/store";

/** Film grain: a small tile of noise, drawn once. */
function useGrain(): string | null {
  return useMemo(() => {
    if (typeof document === "undefined") return null;
    const c = document.createElement("canvas");
    c.width = c.height = 160;
    const g = c.getContext("2d");
    if (!g) return null;
    const img = g.createImageData(160, 160);
    for (let i = 0; i < img.data.length; i += 4) {
      const v = Math.random() * 255;
      img.data[i] = img.data[i + 1] = img.data[i + 2] = v;
      img.data[i + 3] = Math.random() < 0.5 ? 26 : 0;
    }
    g.putImageData(img, 0, 0);
    return c.toDataURL("image/png");
  }, []);
}

/** Vignette, and film grain during cinematic replays: inside the map's stage, under the attribution and legend. */
export function CinemaFilm() {
  const replaying = useApp((s) => !!s.replay);
  const motion = useFx((s) => motionFx(s));
  const level = useFx((s) => s.level);
  const grain = useGrain();
  if (level === "off") return null;
  const film = replaying && motion;
  return (
    <div className={`film${film ? " on" : ""}`} aria-hidden>
      <div className="vignette" />
      {film && grain && <div className="grain" style={{ backgroundImage: `url(${grain})` }} />}
    </div>
  );
}

/** Letterbox bars over the map during cinematic replays. */
export function Letterbox() {
  const level = useFx((s) => s.level);
  if (level === "off") return null;
  return (
    <div className="letterbox" aria-hidden>
      <div className="lb lb-top" />
      <div className="lb lb-bottom" />
    </div>
  );
}

/** The title card as a replay begins. */
export function ReplayIntro() {
  const r = useApp((s) => s.replay);
  const key = r?.key ?? null;
  const [shown, setShown] = useState<string | null>(null);
  useEffect(() => {
    if (!key) { setShown(null); return; }
    setShown(key);
    const id = window.setTimeout(() => setShown(null), 3400);
    return () => window.clearTimeout(id);
  }, [key, r?.def]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!r || shown !== r.key) return null;
  return (
    <div className="intro" aria-hidden>
      <div className="intro-kicker">Replay</div>
      <div className="intro-title">{r.title}</div>
      <div className="intro-rule" />
      <div className="intro-years">{yearsLabel(r.t0, r.t1, false)}</div>
      {r.def?.blurb && <div className="intro-blurb">{r.def.blurb}</div>}
    </div>
  );
}

/** When a replay reaches its end: what it showed, and where to go next. */
export function ReplayEnd() {
  const r = useApp((s) => s.replay);
  const battles = useFx((s) => (s.level === "off" ? 0 : s.stats.battles)); // counted by the effects layer
  const setPanel = useApp((s) => s.setPanel);
  const endReplay = useApp((s) => s.endReplay);
  if (!r || !r.done) return null;
  const last = r.chapters[r.chapters.length - 1];
  return (
    <div className="ending" role="dialog" aria-label={`End of the replay: ${r.title}`}>
      <div className="kicker">End of the replay</div>
      <div className="ending-title">{r.title}</div>
      <div className="ending-years">{yearsLabel(r.t0, r.t1, false)} · {durationLabel(r.t1 - r.t0)}</div>
      {last && <p className="ending-last"><span>{last.d}</span> {last.x}</p>}
      <div className="ending-stats">
        {battles > 0 && <div><strong>{fmtInt(battles)}</strong><span>battles and events on the map</span></div>}
        <div><strong>{r.chapters.length}</strong><span>chapters</span></div>
      </div>
      <div className="ending-actions">
        <button className="primary" onClick={restartReplay}>▶ Play again</button>
        <button onClick={() => { endReplay(); setPanel("replays"); }}>Choose another war</button>
        <button onClick={endReplay}>Explore the map</button>
      </div>
    </div>
  );
}

const STARTERS: { id: string; label: string }[] = [
  { id: "ww2", label: "World War II" },
  { id: "ww1", label: "World War I" },
  { id: "lithuanian-independence-wars", label: "Lithuania 1918–1920" },
];

/** First visit: the best-known replays, one click away, and how the timeline works. */
export function Welcome({ touch, onClose }: { touch: boolean; onClose: () => void }) {
  const replays = useApp((s) => s.replays);
  const setPanel = useApp((s) => s.setPanel);
  const items = STARTERS.map((x) => ({ ...x, def: replays?.find((r) => r.id === x.id) })).filter((x) => x.def);
  return (
    <div className="welcome" role="dialog" aria-label="Welcome">
      <button className="welcome-close" onClick={onClose} aria-label="Close">×</button>
      <div className="kicker">Conflicts Atlas</div>
      <h2>Watch history's wars unfold</h2>
      <p>Battles flare up on the map where and when they happened. Pick a war to replay, with sound:</p>
      <div className="welcome-actions">
        {items.map((x, k) => (
          <button key={x.id} className={k === 0 ? "primary" : ""} onClick={() => { audio.unlock(); onClose(); startReplay({ def: x.def! }); }}>
            <span aria-hidden>▶</span> {x.label}
          </button>
        ))}
        <button className="link" onClick={() => { onClose(); setPanel("replays"); }}>All {replays ? replays.length : ""} replays →</button>
      </div>
      <p className="welcome-tip">
        {touch ? <>Or <strong>swipe the timeline</strong> to travel through 5,000 years; pinch it to zoom in time.</> :
          <>Or <strong>scroll the timeline</strong> to travel through 5,000 years; Ctrl + scroll zooms in time.</>}
      </p>
    </div>
  );
}
